import { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Menu, X, Sun, Moon, BookOpen, ChevronDown
} from 'lucide-react';
import { Show, SignInButton, UserButton } from '@clerk/react';

const LANGS = [
  { code: 'DE' },
  { code: 'FR' },
  { code: 'IT' },
  { code: 'EN' },
] as const;

function FlagIcon({ code, size = 14 }: { code: string; size?: number }) {
  const w = Math.round(size * 1.5);
  const h = size;
  switch (code) {
    case 'EN':
      return (
        <svg width={w} height={h} viewBox="0 0 30 20" style={{ display: 'block', borderRadius: 2 }}>
          <rect width="30" height="20" fill="#012169"/>
          <path d="M0,0 L30,20 M30,0 L0,20" stroke="white" strokeWidth="6"/>
          <path d="M0,0 L30,20 M30,0 L0,20" stroke="#C8102E" strokeWidth="4"/>
          <path d="M15,0 V20 M0,10 H30" stroke="white" strokeWidth="6"/>
          <path d="M15,0 V20 M0,10 H30" stroke="#C8102E" strokeWidth="4"/>
        </svg>
      );
    case 'DE':
      return (
        <svg width={w} height={h} viewBox="0 0 30 20" style={{ display: 'block', borderRadius: 2 }}>
          <rect width="30" height="7" fill="#000"/>
          <rect y="7" width="30" height="6" fill="#DD0000"/>
          <rect y="13" width="30" height="7" fill="#FFCE00"/>
        </svg>
      );
    case 'FR':
      return (
        <svg width={w} height={h} viewBox="0 0 30 20" style={{ display: 'block', borderRadius: 2 }}>
          <rect width="10" height="20" fill="#002395"/>
          <rect x="10" width="10" height="20" fill="#FFFFFF"/>
          <rect x="20" width="10" height="20" fill="#ED2939"/>
        </svg>
      );
    case 'IT':
      return (
        <svg width={w} height={h} viewBox="0 0 30 20" style={{ display: 'block', borderRadius: 2 }}>
          <rect width="10" height="20" fill="#009246"/>
          <rect x="10" width="10" height="20" fill="#FFFFFF"/>
          <rect x="20" width="10" height="20" fill="#CE2B37"/>
        </svg>
      );
    default:
      return null;
  }
}
import { OmniSearch } from '../ui/OmniSearch';
import { NotificationBell } from '../NotificationBell';
import { useAppContext } from '../../context/AppContext';
import { useT } from '../../i18n';

export function Topbar({ onMenuToggle, isSidebarCollapsed }: { onMenuToggle: () => void, isSidebarCollapsed: boolean }) {
  const { uiLang, setUiLang } = useAppContext();
  const t = useT();
  const [theme, setTheme] = useState(() => localStorage.getItem('theme') || 'light');
  const [langOpen, setLangOpen] = useState(false);
  const langRef = useRef<HTMLDivElement>(null);
  const location = useLocation();

  useEffect(() => {
    if (!localStorage.getItem('theme')) {
      const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      setTheme(prefersDark ? 'dark' : 'light');
    }
  }, []);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('theme', theme);
  }, [theme]);

  // Close lang dropdown on outside click
  useEffect(() => {
    if (!langOpen) return;
    const handler = (e: MouseEvent) => {
      if (langRef.current && !langRef.current.contains(e.target as Node)) {
        setLangOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [langOpen]);

  // Note: the API health badge that used to live here moved to
  // Settings → System (developer-only). The topbar now hosts the
  // Documentation link in that slot. See `useApiHealth.ts`.

  return (
    <header className="h-[52px] bg-[var(--surface)] border-b-[1px] border-[var(--border)] flex items-center px-[20px] shrink-0 sticky top-0 z-40 transition-colors duration-200 gap-[16px]">
      <button
        onClick={onMenuToggle}
        className="text-[var(--text-2)] hover:text-[var(--text-1)] lg:hidden shrink-0"
      >
        {isSidebarCollapsed ? <Menu size={20} /> : <X size={20} />}
      </button>

      {/* Centred search — takes all available space between the menu button and right controls */}
      {location.pathname !== '/search' && (
        <div className="hidden sm:flex flex-1 justify-center">
          <div className="w-full max-w-[520px]">
            <OmniSearch />
          </div>
        </div>
      )}
      
      <div className="flex items-center gap-[var(--s4)]">
        {/* Language Selection */}
        <div className="relative" ref={langRef}>
          <button
            onClick={() => setLangOpen(o => !o)}
            className="flex items-center gap-[6px] px-[10px] py-[5px] border border-[var(--border)] rounded-[var(--r2)] text-[11px] font-[family-name:var(--font-mono)] font-bold uppercase text-[var(--text-2)] hover:text-[var(--text-1)] hover:bg-[var(--surface-2)] transition-colors"
          >
            <FlagIcon code={uiLang} size={13} />
            <span>{uiLang}</span>
            <ChevronDown size={11} className={`transition-transform ${langOpen ? 'rotate-180' : ''}`} />
          </button>
          {langOpen && (
            <div className="absolute right-0 top-[calc(100%+4px)] bg-[var(--surface)] border border-[var(--border)] rounded-[var(--r2)] shadow-lg z-50 overflow-hidden min-w-[90px]">
              {LANGS.map(({ code }) => (
                <button
                  key={code}
                  onClick={() => { setUiLang(code); setLangOpen(false); }}
                  className="flex items-center gap-[8px] w-full px-[10px] py-[7px] text-[11px] font-[family-name:var(--font-mono)] font-bold uppercase transition-colors hover:bg-[var(--surface-2)]"
                  style={uiLang === code
                    ? { color: 'var(--accent)' }
                    : { color: 'var(--text-2)' }
                  }
                >
                  <FlagIcon code={code} size={13} />
                  <span>{code}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Documentation — replaces the old API status badge. Opens
            /documentation in a NEW TAB so the user keeps their current
            dashboard context (filters, chat, watchlist work) while reading
            the manual side-by-side. */}
        <a
          href="/documentation"
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-[var(--s2)] bg-[var(--surface-2)] border-[1px] border-[var(--border)] px-[12px] py-[5px] rounded-[var(--r2)] text-[var(--text-2)] hover:text-[var(--text-1)] hover:border-[var(--accent)] transition-colors"
        >
          <BookOpen size={14} />
          <span className="font-[family-name:var(--font-mono)] text-[11px] uppercase leading-none mt-[2px]">
            {t('nav.documentation')}
          </span>
        </a>
        
        {/* Theme Toggle */}
        <button
          onClick={() => setTheme(prev => prev === 'light' ? 'dark' : 'light')}
          className="w-[32px] h-[32px] flex items-center justify-center rounded-[var(--r1)] text-[var(--text-2)] hover:bg-[var(--surface-2)] hover:text-[var(--text-1)] transition-colors"
        >
          {theme === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
        </button>

        {/* Notification bell — only for signed-in users; polls
            /v1/notifications/bundle every 60s + on tab focus. */}
        <Show when="signed-in">
          <NotificationBell />
        </Show>

        {/* Auth status and user profile */}
        <Show when="signed-out">
          <SignInButton mode="modal">
            <button className="px-[12px] py-[5px] rounded-[var(--r2)] bg-[var(--accent)] text-white text-[12px] font-medium hover:opacity-90 transition-opacity">
              {t('topbar.signIn', 'Sign In')}
            </button>
          </SignInButton>
        </Show>
        <Show when="signed-in">
          <UserButton />
        </Show>
      </div>
    </header>
  );
}
